/**
 * 认证工具函数工厂
 *
 *
 * 通过配置适配不同的认证方式。
 */

/**
 * AuthManager 配置
 */
export interface AuthManagerConfig {
  /** token/session 在 localStorage 中的 key */
  tokenKey: string;
  /** 过期时间存储 key (可选，用于 session 有效期管理) */
  expireKey?: string;
  /** 会话有效期 (毫秒)，不设置则不自动过期 */
  ttlMs?: number;
  /** 需要在 clearAuth 时额外清除的 localStorage key 列表 */
  extraStorageKeys?: string[];
  /** 登出后的回调 (如跳转登录页) */
  onUnauthorized?: () => void;
}

/**
 * AuthManager 实例
 */
export interface AuthManager {
  /** 获取 token (自动检查过期) */
  getToken: () => string | null;
  /** 设置 token (自动写入过期时间) */
  setToken: (token: string) => void;
  /** 清除所有认证信息 */
  clearAuth: () => void;
  /** 处理未授权状态 (清除认证 + 执行 onUnauthorized 回调) */
  handleUnauthorized: () => void;
  /** 检查是否已认证 */
  isAuthenticated: () => boolean;
}

/**
 * 创建认证管理器
 *
 * @param config - 认证配置
 * @returns AuthManager 实例
 *
 * @example
 * ```ts
 * // mc-guide 风格 (session + 过期时间)
 * const auth = createAuthManager({
 *   tokenKey: 'sessionId',
 *   expireKey: 'sessionExpireAt',
 *   ttlMs: 30 * 24 * 60 * 60 * 1000, // 30天
 *   extraStorageKeys: ['isAdmin'],
 *   onUnauthorized: () => history.push('/user/login'),
 * });
 *
 * // ruiying 风格 (token + 额外数据)
 * const auth = createAuthManager({
 *   tokenKey: 'token',
 *   extraStorageKeys: ['userButtons', 'userMenus', 'userRoles', 'userId', 'userName'],
 *   onUnauthorized: () => emitLoginRequired(),
 * });
 *
 * // keel-cloud 风格 (Bearer token)
 * const auth = createAuthManager({
 *   tokenKey: 'kc_auth_token',
 *   onUnauthorized: () => history.push('/login'),
 * });
 *
 * // 使用
 * auth.setToken('abc123');
 * const token = auth.getToken(); // 'abc123'
 * auth.clearAuth(); // 清除所有
 * ```
 */
/** 安全获取 localStorage（SSR 环境返回 null） */
function safeStorage() {
  try {
    return typeof window !== 'undefined' ? window.localStorage : null;
  } catch {
    return null;
  }
}

export function createAuthManager(config: AuthManagerConfig): AuthManager {
  const {
    tokenKey,
    expireKey,
    ttlMs,
    extraStorageKeys = [],
    onUnauthorized,
  } = config;

  function getToken(): string | null {
    const storage = safeStorage();
    if (!storage) return null;
    const token = storage.getItem(tokenKey);
    if (!token) return null;

    // 检查过期
    if (expireKey && ttlMs) {
      const expireAt = safeStorage()?.getItem(expireKey) ?? null;
      if (expireAt) {
        const expireTime = Number.parseInt(expireAt, 10);
        if (!Number.isNaN(expireTime) && Date.now() > expireTime) {
          clearAuth();
          return null;
        }
      }
    }

    return token;
  }

  function setToken(token: string): void {
    if (!token) return;
    const storage = safeStorage();
    if (!storage) return;
    storage.setItem(tokenKey, token);

    // 写入过期时间
    if (expireKey && ttlMs) {
      const expireAt = Date.now() + ttlMs;
      storage.setItem(expireKey, String(expireAt));
    }
  }

  function clearAuth(): void {
    const storage = safeStorage();
    if (!storage) return;
    storage.removeItem(tokenKey);
    if (expireKey) {
      storage.removeItem(expireKey);
    }
    // 清除额外存储
    for (const key of extraStorageKeys) {
      storage.removeItem(key);
    }
  }

  function handleUnauthorized(): void {
    clearAuth();
    onUnauthorized?.();
  }

  function isAuthenticated(): boolean {
    return getToken() !== null;
  }

  return {
    getToken,
    setToken,
    clearAuth,
    handleUnauthorized,
    isAuthenticated,
  };
}

/**
 * 登录事件常量 (用于跨组件通信触发登录模态框)
 */
export const LOGIN_REQUIRED_EVENT = 'auth:login-required';

/**
 * 触发登录请求事件
 *
 * @example
 * ```ts
 * // 在 request interceptor 中
 * emitLoginRequired();
 *
 * // 在组件中监听
 * window.addEventListener('auth:login-required', () => setLoginVisible(true));
 * ```
 */
export function emitLoginRequired(): void {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(new CustomEvent(LOGIN_REQUIRED_EVENT));
}
